/**
 * PaperMapReviewer Component
 * 
 * Displays paper images with detected question bounding boxes overlaid.
 * Allows the user to review, edit, and confirm the AI-detected regions before continuing.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Region {
  id: string;
  questionId: string;
  questionLabel?: string;
  pageIndex: number;
  bbox: BoundingBox;
  confidence: number;
}

interface PaperMapReviewerProps {
  assignmentId: string;
  paperMapId: string;
  regions: Region[];
  imageUrls: string[];
  onConfirm: (editedRegions: Region[]) => void;
  confirming?: boolean;
}

// Colors for different question regions
const REGION_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
];

type DragMode = 'move' | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se' | 'resize-n' | 'resize-s' | 'resize-e' | 'resize-w' | null;

export function PaperMapReviewer({
  assignmentId,
  paperMapId,
  regions: initialRegions,
  imageUrls,
  onConfirm,
  confirming = false,
}: PaperMapReviewerProps) {
  const [selectedPage, setSelectedPage] = useState(0);
  const [imageLoaded, setImageLoaded] = useState<boolean[]>([]);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number }[]>([]);
  
  // Editing state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedRegions, setEditedRegions] = useState<Region[]>(initialRegions);
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Drag state
  const [dragMode, setDragMode] = useState<DragMode>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [originalBbox, setOriginalBbox] = useState<BoundingBox | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const initialRegionsRef = useRef<Region[]>(initialRegions);
  const isDraggingRef = useRef(false);

  // Create a stable key from initial regions to detect actual changes
  const initialRegionsKey = useMemo(() => 
    JSON.stringify(initialRegions.map(r => r.id).sort()),
    [initialRegions]
  );

  // Reset edited regions only when the actual region IDs change (not on every re-render)
  useEffect(() => {
    // Don't reset if we're in the middle of dragging
    if (isDraggingRef.current) return;
    
    // Check if regions actually changed (by comparing IDs)
    const prevIds = initialRegionsRef.current.map(r => r.id).sort().join(',');
    const newIds = initialRegions.map(r => r.id).sort().join(',');
    
    if (prevIds !== newIds) {
      setEditedRegions(initialRegions);
      setHasChanges(false);
      initialRegionsRef.current = initialRegions;
    }
  }, [initialRegionsKey, initialRegions]);

  // Group regions by page
  const regionsByPage = editedRegions.reduce<Record<number, Region[]>>((acc, region) => {
    if (!acc[region.pageIndex]) {
      acc[region.pageIndex] = [];
    }
    acc[region.pageIndex]!.push(region);
    return acc;
  }, {});

  const handleImageLoad = (index: number, e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageLoaded((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
    setImageDimensions((prev) => {
      const next = [...prev];
      next[index] = { width: img.naturalWidth, height: img.naturalHeight };
      return next;
    });
  };

  const currentPageRegions = regionsByPage[selectedPage] || [];

  // Get mouse position relative to container as percentage
  const getRelativePosition = useCallback((e: React.MouseEvent | MouseEvent) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  }, []);

  // Start dragging
  const handleMouseDown = useCallback((
    e: React.MouseEvent,
    regionId: string,
    mode: DragMode
  ) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.stopPropagation();
    
    const region = editedRegions.find(r => r.id === regionId);
    if (!region) return;
    
    setSelectedRegionId(regionId);
    setDragMode(mode);
    setDragStart(getRelativePosition(e));
    setOriginalBbox({ ...region.bbox });
    isDraggingRef.current = true;
  }, [isEditMode, editedRegions, getRelativePosition]);

  // Handle mouse move during drag
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragMode || !dragStart || !originalBbox || !selectedRegionId) return;
    
    const currentPos = getRelativePosition(e);
    const deltaX = currentPos.x - dragStart.x;
    const deltaY = currentPos.y - dragStart.y;
    
    setEditedRegions(prev => prev.map(region => {
      if (region.id !== selectedRegionId) return region;
      
      const newBbox = { ...originalBbox };
      
      switch (dragMode) {
        case 'move':
          newBbox.x = Math.max(0, Math.min(1 - originalBbox.width, originalBbox.x + deltaX));
          newBbox.y = Math.max(0, Math.min(1 - originalBbox.height, originalBbox.y + deltaY));
          break;
        case 'resize-se':
          newBbox.width = Math.max(0.02, Math.min(1 - originalBbox.x, originalBbox.width + deltaX));
          newBbox.height = Math.max(0.02, Math.min(1 - originalBbox.y, originalBbox.height + deltaY));
          break;
        case 'resize-sw': {
            const newWidthSW = Math.max(0.02, originalBbox.width - deltaX);
            newBbox.x = Math.max(0, originalBbox.x + originalBbox.width - newWidthSW);
            newBbox.width = newWidthSW;
            newBbox.height = Math.max(0.02, Math.min(1 - originalBbox.y, originalBbox.height + deltaY));
            break;
        }
        case 'resize-ne': {
            newBbox.width = Math.max(0.02, Math.min(1 - originalBbox.x, originalBbox.width + deltaX));
            const newHeightNE = Math.max(0.02, originalBbox.height - deltaY);
            newBbox.y = Math.max(0, originalBbox.y + originalBbox.height - newHeightNE);
            newBbox.height = newHeightNE;
            break;
        }
        case 'resize-nw': {
            const newWidthNW = Math.max(0.02, originalBbox.width - deltaX);
            newBbox.x = Math.max(0, originalBbox.x + originalBbox.width - newWidthNW);
            newBbox.width = newWidthNW;
            const newHeightNW = Math.max(0.02, originalBbox.height - deltaY);
            newBbox.y = Math.max(0, originalBbox.y + originalBbox.height - newHeightNW);
            newBbox.height = newHeightNW;
            break;
        }
        case 'resize-n': {
            const newHeightN = Math.max(0.02, originalBbox.height - deltaY);
            newBbox.y = Math.max(0, originalBbox.y + originalBbox.height - newHeightN);
            newBbox.height = newHeightN;
            break;
        }
        case 'resize-s':
          newBbox.height = Math.max(0.02, Math.min(1 - originalBbox.y, originalBbox.height + deltaY));
          break;
        case 'resize-e':
          newBbox.width = Math.max(0.02, Math.min(1 - originalBbox.x, originalBbox.width + deltaX));
          break;
        case 'resize-w': {
            const newWidthW = Math.max(0.02, originalBbox.width - deltaX);
            newBbox.x = Math.max(0, originalBbox.x + originalBbox.width - newWidthW);
            newBbox.width = newWidthW;
            break;
        }
      }
      
      return { ...region, bbox: newBbox, confidence: 1.0 }; // Human-edited = full confidence
    }));
    
    setHasChanges(true);
  }, [dragMode, dragStart, originalBbox, selectedRegionId, getRelativePosition]);

  // End dragging
  const handleMouseUp = useCallback(() => {
    setDragMode(null);
    setDragStart(null);
    setOriginalBbox(null);
    isDraggingRef.current = false;
  }, []);

  // Add/remove mouse event listeners
  useEffect(() => {
    if (dragMode) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragMode, handleMouseMove, handleMouseUp]);

  // Delete selected region
  const handleDeleteRegion = useCallback(() => {
    if (!selectedRegionId) return;
    setEditedRegions(prev => prev.filter(r => r.id !== selectedRegionId));
    setSelectedRegionId(null);
    setHasChanges(true);
  }, [selectedRegionId]);

  // Reset to original regions
  const handleReset = useCallback(() => {
    setEditedRegions(initialRegions);
    setSelectedRegionId(null);
    setHasChanges(false);
  }, [initialRegions]);

  // Handle confirm with edited regions
  const handleConfirm = useCallback(() => {
    onConfirm(editedRegions);
  }, [onConfirm, editedRegions]);

  // Click on container to deselect
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedRegionId(null);
    }
  }, []);

  // Resize handle component
  const ResizeHandle = ({ position, regionId }: { position: DragMode; regionId: string }) => {
    const positionStyles: Record<string, React.CSSProperties> = {
      'resize-nw': { top: -4, left: -4, cursor: 'nwse-resize' },
      'resize-ne': { top: -4, right: -4, cursor: 'nesw-resize' },
      'resize-sw': { bottom: -4, left: -4, cursor: 'nesw-resize' },
      'resize-se': { bottom: -4, right: -4, cursor: 'nwse-resize' },
      'resize-n': { top: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
      'resize-s': { bottom: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
      'resize-e': { right: -4, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' },
      'resize-w': { left: -4, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' },
    };

    return (
      <Box
        onMouseDown={(e) => handleMouseDown(e, regionId, position)}
        sx={{
          position: 'absolute',
          width: 8,
          height: 8,
          backgroundColor: 'white',
          border: '2px solid #1976d2',
          borderRadius: '50%',
          zIndex: 10,
          ...positionStyles[position!],
        }}
      />
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            確認題目區域
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {hasChanges && (
              <Chip 
                label="已修改" 
                color="warning" 
                size="small" 
                variant="outlined"
              />
            )}
            <Chip 
              label={`共 ${editedRegions.length} 個區域`} 
              color="primary" 
              size="small" 
            />
          </Box>
        </Box>

        <Alert severity={isEditMode ? "warning" : "info"} sx={{ mb: 2 }}>
          {isEditMode 
            ? "編輯模式：點擊選擇區域，拖曳移動位置，拖曳角落調整大小。完成後點擊鎖定圖示。"
            : "AI 已識別出答題區域（以彩色框線標示）。請檢查是否正確，如需調整請點擊編輯按鈕。"
          }
        </Alert>

        {/* Page tabs */}
        {imageUrls.length > 1 && (
          <Tabs
            value={selectedPage}
            onChange={(_, newValue) => setSelectedPage(newValue)}
            sx={{ mb: 2 }}
          >
            {imageUrls.map((_, index) => (
              <Tab 
                key={index} 
                label={`第 ${index + 1} 頁`}
                icon={
                  <Chip 
                    label={regionsByPage[index]?.length || 0} 
                    size="small" 
                    sx={{ ml: 1 }}
                  />
                }
                iconPosition="end"
              />
            ))}
          </Tabs>
        )}

        {/* Image with bounding boxes */}
        <Box
          ref={containerRef}
          onClick={handleContainerClick}
          sx={{
            position: 'relative',
            width: '100%',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            overflow: 'visible',
            mb: 2,
            cursor: isEditMode ? 'default' : 'default',
            userSelect: 'none',
          }}
        >
          <img
            src={imageUrls[selectedPage]}
            alt={`Page ${selectedPage + 1}`}
            onLoad={(e) => handleImageLoad(selectedPage, e)}
            draggable={false}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              pointerEvents: 'none',
            }}
          />

          {/* Bounding box overlays */}
          {imageLoaded[selectedPage] && currentPageRegions.map((region, index) => {
            const isSelected = selectedRegionId === region.id;
            const color = REGION_COLORS[editedRegions.findIndex(r => r.id === region.id) % REGION_COLORS.length];
            
            return (
            <Box
              key={region.id}
                onClick={(e) => {
                  if (isEditMode) {
                    e.stopPropagation();
                    setSelectedRegionId(region.id);
                  }
                }}
                onMouseDown={(e) => {
                  if (isEditMode && !dragMode) {
                    handleMouseDown(e, region.id, 'move');
                  }
                }}
              sx={{
                position: 'absolute',
                left: `${region.bbox.x * 100}%`,
                top: `${region.bbox.y * 100}%`,
                width: `${region.bbox.width * 100}%`,
                height: `${region.bbox.height * 100}%`,
                  border: `3px solid ${color}`,
                  backgroundColor: `${color}${isSelected ? '40' : '20'}`,
                boxSizing: 'border-box',
                  cursor: isEditMode ? (dragMode === 'move' ? 'grabbing' : 'grab') : 'default',
                  outline: isSelected ? '2px solid #1976d2' : 'none',
                  outlineOffset: 2,
                  transition: dragMode ? 'none' : 'outline 0.15s ease',
              }}
            >
              {/* Question label */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -24,
                  left: 0,
                    backgroundColor: color,
                  color: 'white',
                  px: 1,
                  py: 0.25,
                  fontSize: '12px',
                  fontWeight: 'bold',
                  borderRadius: '4px 4px 0 0',
                  whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                }}
              >
                {region.questionLabel || region.questionId}
                  {region.confidence < 1.0 && (
                    <span style={{ opacity: 0.8, fontSize: '10px' }}>
                    ({Math.round(region.confidence * 100)}%)
                  </span>
                  )}
                </Box>

                {/* Resize handles (only in edit mode and when selected) */}
                {isEditMode && isSelected && (
                  <>
                    <ResizeHandle position="resize-nw" regionId={region.id} />
                    <ResizeHandle position="resize-ne" regionId={region.id} />
                    <ResizeHandle position="resize-sw" regionId={region.id} />
                    <ResizeHandle position="resize-se" regionId={region.id} />
                    <ResizeHandle position="resize-n" regionId={region.id} />
                    <ResizeHandle position="resize-s" regionId={region.id} />
                    <ResizeHandle position="resize-e" regionId={region.id} />
                    <ResizeHandle position="resize-w" regionId={region.id} />
                  </>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Region list */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          識別的題目區域：
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {editedRegions.map((region, index) => (
            <Chip
              key={region.id}
              label={region.questionLabel || region.questionId}
              size="small"
              variant={selectedRegionId === region.id ? "filled" : "outlined"}
              sx={{
                backgroundColor: selectedRegionId === region.id 
                  ? REGION_COLORS[index % REGION_COLORS.length]
                  : `${REGION_COLORS[index % REGION_COLORS.length]}20`,
                borderColor: REGION_COLORS[index % REGION_COLORS.length],
                borderWidth: 2,
                borderStyle: 'solid',
                color: selectedRegionId === region.id ? 'white' : 'inherit',
                '&:hover': {
                  backgroundColor: `${REGION_COLORS[index % REGION_COLORS.length]}40`,
                },
              }}
              onClick={() => {
                setSelectedPage(region.pageIndex);
                if (isEditMode) {
                  setSelectedRegionId(region.id);
                }
              }}
              onDelete={isEditMode ? () => {
                setEditedRegions(prev => prev.filter(r => r.id !== region.id));
                setHasChanges(true);
                if (selectedRegionId === region.id) {
                  setSelectedRegionId(null);
                }
              } : undefined}
            />
          ))}
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditMode && (
              <>
                <Tooltip title="刪除選中區域">
                  <span>
                    <IconButton
                      onClick={handleDeleteRegion}
                      disabled={!selectedRegionId}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="還原所有修改">
                  <span>
                    <IconButton
                      onClick={handleReset}
                      disabled={!hasChanges}
                    >
                      <UndoIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
              variant={isEditMode ? "contained" : "outlined"}
              color={isEditMode ? "warning" : "primary"}
              startIcon={isEditMode ? <LockIcon /> : <LockOpenIcon />}
              onClick={() => {
                setIsEditMode(!isEditMode);
                if (isEditMode) {
                  setSelectedRegionId(null);
                }
              }}
            >
              {isEditMode ? '鎖定編輯' : '編輯區域'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={confirming ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
              onClick={handleConfirm}
              disabled={confirming || isEditMode}
          >
            {confirming ? '處理中...' : '確認並繼續'}
          </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
